// The assistant's own wording lives in the backend (reader: chat_ui.json,
// served from /chat/ui), so a second client shows the same assistant without
// the text being copied here and then drifting. What is left in this file is
// behaviour: which language to ask for, how to fill a template, and how to
// shape the data the widget renders.

import api from "./api";

// Just enough to render a usable widget when /chat/ui cannot be reached. Not a
// translation table — one language, no starter questions. If this is what the
// user sees, something is wrong and it should look plain rather than complete.
export const FALLBACK_STRINGS = {
  locale: "en-GB",
  // Deliberately empty. This is sent to /chat as the language to reply in, and
  // if the bundle failed to load we do not actually know what that is —
  // claiming English here would answer a Turkish question in English. Left
  // blank, the backend skips its REPLY LANGUAGE instruction and the assistant
  // follows whatever language the user wrote in.
  languageName: "",
  title: "mongrel.intelligence",
  greeting: "Ask me anything about your channels.",
  empty: "",
  insightIntro: "",
  insightNew: "new",
  insightOutro: "",
  placeholder: "Type your question…",
  placeholderExhausted: "Daily limit reached",
  clear: "Clear conversation",
  clearConfirm: "Tap again to delete",
  showStarters: "Suggested questions",
  rateUp: "Good answer",
  rateDown: "Bad answer",
  rateThanks: "Thanks for the feedback.",
  genericError: "Sorry, something went wrong. Please try again.",
  quotaRemaining: "{n} questions left today.",
  quotaRemainingOne: "1 question left today.",
  quotaExhausted: "You've used today's questions — they reset at midnight.",
  quotaBurst: "You're asking too quickly. Please wait {sec} seconds and try again.",
  quotaDaily: "You've used your {limit} questions for today. They reset at midnight.",
  starters: [],
};

/**
 * The language to ask the backend for: the user's own panel setting first, then
 * whatever the browser reports. Sent as-is — "tr-TR" and "TR" are both fine —
 * because the backend does the matching and the fallback. Adding a language is
 * then a backend-only change; this file never learns its code.
 */
export function preferredChatLang(user) {
  const candidates = [
    user?.language,
    typeof navigator !== "undefined" ? navigator.language : "",
    ...(typeof navigator !== "undefined" && Array.isArray(navigator.languages)
      ? navigator.languages
      : []),
  ];
  return candidates.find((c) => c) || "en";
}

/** Fetches the assistant's text. Falls back rather than throwing. */
export async function fetchChatStrings(user) {
  try {
    const res = await api.get("/chat/ui", {
      params: { lang: preferredChatLang(user) },
    });
    if (res.data?.strings) return { ...FALLBACK_STRINGS, ...res.data.strings };
  } catch (err) {
    console.warn("[chat] could not load assistant text:", err?.message || err);
  }
  return FALLBACK_STRINGS;
}

// Templates carry {n}, {sec}, {limit} because JSON cannot hold a function.
function fill(template, values) {
  return String(template || "").replace(/\{(\w+)\}/g, (whole, key) =>
    key in values ? values[key] : whole
  );
}

export function quotaRemainingText(t, n) {
  return n === 1 ? t.quotaRemainingOne : fill(t.quotaRemaining, { n });
}

// Flattened question -> follow-ups, so a chip can be looked up regardless of
// which set it came from.
function followUpMap(t) {
  const map = {};
  for (const item of t.starters || []) map[item.q] = item.next;
  return map;
}

/**
 * What to offer next. Before anything is asked, the starter questions; after an
 * answer, the follow-ups belonging to the question that was clicked. Clicking a
 * follow-up would otherwise dead-end, so those fall back to their unused
 * siblings — still the same topic, without needing a third level of content.
 * A freely typed question belongs to no branch and gets nothing, rather than
 * something unrelated.
 */
export function suggestionsFor(t, messages, limit = 3) {
  const starters = t.starters || [];

  // Nothing asked yet. The pane is not necessarily empty — the day's figures
  // are already in it — but there is no question for follow-ups to build on.
  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  if (!lastUser) return starters.map((s) => s.q);

  const asked = new Set(
    messages.filter((m) => m.role === "user").map((m) => m.content)
  );

  const map = followUpMap(t);
  let branch = map[lastUser.content];

  if (!branch) {
    branch = Object.values(map).find((next) => next.includes(lastUser.content));
  }
  if (!branch) return [];

  return branch.filter((q) => !asked.has(q)).slice(0, limit);
}

// When a message was sent, in the chat's language. Anything from an earlier day
// carries its date as well: the conversation now spans days, and a bare "09:12"
// on last week's message says nothing.
//
// Pinned to Istanbul like every other figure in the panel, so a user abroad
// reads the same clock the data is reported on.
export function formatMessageTime(t, at) {
  if (!at) return "";
  const when = new Date(at);
  if (Number.isNaN(when.getTime())) return "";

  const clock = { hour: "2-digit", minute: "2-digit", timeZone: PANEL_TZ };
  const today = dayInPanelTz(new Date());
  const day = dayInPanelTz(when);

  let options = clock;
  if (day !== today) {
    options = { day: "numeric", month: "short", ...clock };
    if (day.slice(0, 4) !== today.slice(0, 4)) options.year = "numeric";
  }

  try {
    return new Intl.DateTimeFormat(t.locale || "en-GB", options).format(when);
  } catch {
    return when.toLocaleString();
  }
}

const PANEL_TZ = "Europe/Istanbul";

function dayInPanelTz(date) {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: PANEL_TZ }).format(date);
  } catch {
    return date.toDateString();
  }
}

// The server returns quota state as data, not prose, so the message can be
// rendered in whichever language the assistant is speaking.
export function quotaMessage(t, quota, fallback) {
  if (!quota) return fallback;
  if (quota.reason === "burst") return fill(t.quotaBurst, { sec: quota.retryInSec || 60 });
  if (quota.reason === "daily") return fill(t.quotaDaily, { limit: quota.limit });
  return fallback;
}
